import { useEffect, useState } from "react";
import {
  LuLibrary,
  LuArrowRight,
  LuChevronLeft,
  LuChevronRight,
  LuMoreHorizontal,
  LuSearch,
} from "react-icons/lu";
import apiClient from "../../../api-services/ApiClient";
import Preloader from "../../../app-components/preloader/Preloader";
import { OptionProps, ParamInterface } from "../../../interface-and-types";
import { ToastContainer } from "react-toastify";
import { formatDate } from "../../../helpers";
import { SITELINKS } from "../../../app-routes/links";
import { Link } from "react-router-dom";
import { ExcelFile, ExcelSheet, ExcelColumn } from "react-xlsx-wrapper";
import { RiFileExcel2Line } from "react-icons/ri";

const InspectionApprovalHistory = () => {
  const [inspectionForm, setInspectionForm] = useState<any[]>([]);
  const [inspectionExport, setInspectionExport] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [searchInput, setSearchInput] = useState<string>("");
  const [searchInputDebounce, setSearchInputDebounce] = useState<string>("");
  const [vessels, setVessels] = useState<OptionProps[]>([]);
  const [inspectionTypeOptions, setInspectionTypeOptions] = useState<
    OptionProps[]
  >([]);
  const [vesselName, setVesselName] = useState<string>("");
  const [inspectionType, setInspectionType] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(false);

  // getInspectionForm
  useEffect(() => {
    const params = {
      Status: "Pending",
      page: currentPage,
      SearchString: searchInput,
      OrderBy: "Descending",
      // VesselName: vesselName,
      // InspectionType: inspectionType,
      PageSize: pageSize,
    };

    getInspectionForm(params);
  }, [currentPage, searchInput, vesselName, inspectionType, pageSize]);

  useEffect(() => {
    getVessels();
    InspectionType();
  }, []);

  // debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchInput(searchInputDebounce);
      setCurrentPage(1);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInputDebounce]);

  const getInspectionForm = async (params: ParamInterface) => {
    setLoading(true);
    try {
      const res: any = await apiClient.get(
        "InspectionApproval/MarineApprover",
        { params }
      );
      if (res && res.data) {
        setInspectionForm(res.data.data);
        setCurrentPage(res.data.pageIndex);
        setLastPage(res.data.totalPages);

        const processedData = res?.data?.data.map((item: any) => ({
          dateOfInspection: item.inspectionForm.dateOfInspection,
          vesselName: item.inspectionForm.vessel?.name,
          vesselType: item.inspectionForm.vessel?.vesselType,
          inspectorName: `${item.inspectionApprover.user.firstName} ${item.inspectionApprover.user.lastName}`,
          inspectionType: item.inspectionForm.inspectionType?.name,
          formState: item.status,
          placeOfInspection: item.inspectionForm.placeOfInspection,
        }));

        setInspectionExport(processedData);
      } else {
        setInspectionForm([]);
      }
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
      setInspectionForm([]);
    } finally {
      setLoading(false);
    }
  };

  const getVessels = async () => {
    try {
      const res: any = await apiClient.get(
        "Vessels?PageSize=1000&view=withuser"
      );
      if (res && res.data) {
        const options: OptionProps[] = res.data.data.map(
          (i: any, index: number) => ({
            label: i.name,
            value: i.id,
            name: "Vessels",
            key: index,
          })
        );
        setVessels(options);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  const InspectionType = async () => {
    try {
      const res: any = await apiClient.get("InspectionType");
      if (res && res.data) {
        const options: OptionProps[] = res.data.data.map(
          (i: any, index: number) => ({
            label: i.name,
            value: i.id,
            name: "InspectionType",
            key: index,
          })
        );
        setInspectionTypeOptions(options);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  // Handle Previous Button Click
  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Handle Next Button Click
  const handleNext = () => {
    if (currentPage < lastPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <>
      <section className="flex justify-between items-center bg-gradient-to-r from-secondaryBlue to-primaryBlue shadow-md border-b border-gray-200">
        <div className="xl:container xl:mx-auto md:px-4 py-4 pb-24 flex justify-between items-center">
          <h4 className="lg:text-[16px] text-white nunito font-medium">
            Inspection Approval History
          </h4>

          {/* export to excel area */}
          <ExcelFile
            filename={"Inspection-History"}
            element={
              <button className="border-white border-2 flex gap-2 p-2 px-4 rounded-md text-sm items-center text-white font-medium hover:shadow-xl shadow">
                <RiFileExcel2Line />{" "}
                <span className="text-xs">Export Table</span>
              </button>
            }
          >
            <ExcelSheet data={inspectionExport} name="Inspection History">
              <ExcelColumn
                label="Date of Inspection"
                value="dateOfInspection"
              />
              <ExcelColumn label="Vessel Name" value="vesselName" />
              <ExcelColumn label="Vessel Type" value="vesselType" />
              <ExcelColumn label="Inspector Name" value="inspectorName" />
              <ExcelColumn
                label="Marine Superintendent"
                value="inspectionType"
              />
              <ExcelColumn label="Form State" value="formState" />
              <ExcelColumn
                label="Place of Inspection"
                value="placeOfInspection"
              />
            </ExcelSheet>
          </ExcelFile>
        </div>
      </section>

      {/* page content */}
      <section className="pt-5 mx-auto md:px-4 xl:container relative top-[-100px] xl:top-[-90px]">
        <div className="rounded-lg pb-5 bg-white shadow-sm border">
          {/* page filter && search */}
          <div className="p-4 2xl:p-6 flex justify-between">
            <div className="relative xl:w-1/3 w-1/2">
              <input
                value={searchInputDebounce}
                onChange={(e) => setSearchInputDebounce(e.target.value)}
                placeholder="Search by vessel name, inspection officer"
                className="p-2 text-xs border border-gray-300 hover:border-blue-400 rounded-lg w-full"
              />
              <LuSearch className="text-lg top-[7px] absolute right-[9px] text-gray-400" />
            </div>

            <div className="flex gap-2">
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                id="entries"
                className="p-2 py-1.5 text-xs border border-gray-300 hover:border-blue-400 rounded-lg"
              >
                <option value="10">Show 10 entries</option>
                <option value="25">Show 25 entries</option>
                <option value="50">Show 50 entries</option>
                <option value="100">Show 100 entries</option>
                <option value="250">Show 250 entries</option>
                <option value="500">Show 500 entries</option>
                <option value="1000">Show 1000 entries</option>
              </select>
              <select
                className="p-2 text-xs border border-gray-300 hover:border-blue-400 rounded-lg"
                onChange={(e) => {
                  setVesselName(e.target.value);
                  setCurrentPage(1);
                }}
                value={vesselName}
              >
                <option value="" disabled>
                  Filter by vessel name
                </option>
                <option value="">All</option>
                {vessels.map((item, i) => {
                  return (
                    <option key={i} value={item.label}>
                      {item.label}
                    </option>
                  );
                })}
              </select>

              <select
                className="p-2 text-xs border border-gray-300 hover:border-blue-400 rounded-lg"
                onChange={(e) => {
                  setInspectionType(e.currentTarget.value);
                  setCurrentPage(1);
                }}
                value={inspectionType}
              >
                <option value="" disabled>
                  Filter by inspection type
                </option>
                <option value="">All</option>
                {inspectionTypeOptions.map((item, i) => {
                  return (
                    <option key={i} value={item.label}>
                      {item.label}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* record TABLE */}
          <div className="overflow-x-auto mb-5 overflow-y-auto">
            <table className="min-w-full bg-white mb-4">
              <thead>
                <tr className="w-full bg-slate-100 text-slate-600 capitalize text-[13px] leading-normal">
                  <th className="py-3 px-3 text-left font-medium">Form Id</th>
                  <th className="py-3 px-3 text-left font-medium">
                    Vessel name
                  </th>
                  <th className="py-3 px-3 text-left font-medium">IMO nos.</th>
                  <th className="py-3 px-3 text-left font-medium">Date</th>
                  <th className="py-3 px-3 text-left font-medium">Status</th>
                  {/* <th className="py-3 px-3 text-left font-medium">
                    Marine Approver
                  </th> */}
                  <th className="py-3 px-3 text-left font-medium">
                    Place of inspection
                  </th>
                  <th className="py-3 px-3 text-left font-medium">
                    Type of inspection
                  </th>
                  <th className="py-3 px-3 text-left font-medium">
                    Form State
                  </th>
                  <th className="py-3 px-3 text-left font-medium">Actions</th>
                </tr>
              </thead>

              {!!inspectionForm?.length && (
                <tbody className="text-slate-600 text-xs font-light">
                  {inspectionForm?.map((inspection: any, i: number) => {
                    return (
                      <tr
                        key={inspection.inspectionForm.inspectionFormNumber}
                        className={`border-b border-[#F2F1F1] hover:bg-gray-100 font-normal text-[#232630]`}
                      >
                        <td className="py-2 px-3 text-left align-top">
                          {inspection.inspectionForm.inspectionFormNumber ?? ""}
                        </td>

                        <td className="py-3 px-3 text-left">
                          {inspection?.inspectionForm?.vessel?.name ?? ""}
                        </td>

                        <td className="py-3 px-3 text-left">
                          {inspection?.inspectionForm?.vessel?.imoNumber ?? ""}
                        </td>

                        <td className="py-3 px-3 text-left">
                          {inspection?.dateOfInspection
                            ? formatDate(
                                inspection?.inspectionForm?.dateOfInspection,
                                "date"
                              )
                            : ""}
                        </td>

                        <td className="py-3 px-3 text-left">
                          {inspection?.inspectionForm?.formState ?? ""}
                        </td>

                        {/* <td className="py-3 px-3 text-left">
                          {inspection?.user?.firstName ?? ""}{" "}
                          {inspection?.user?.lastName ?? ""}
                        </td> */}

                        <td className="py-3 px-3 text-left">
                          {inspection?.inspectionForm?.placeOfInspection ?? ""}
                        </td>

                        <td className="py-3 px-3 text-left">
                          {inspection?.inspectionForm?.inspectionType?.name ??
                            ""}
                        </td>

                        <td className="py-2 px-3 text-left align-top">
                          {inspection?.inspectionForm?.formState ?? ""}
                        </td>

                        <td className="py-2 px-2 text-left flex gap-4 items-left">
                          <Link
                            to={`${SITELINKS.ASSESSMENT_FORM}/${inspection?.inspectionForm?.id}`}
                            className={`font-medium flex gap-1 items-center text-red-600 hover:text-blue-800`}
                          >
                            <span>
                              {inspection?.inspectionForm?.inspectionType?.name}{" "}
                              Form
                            </span>
                            <LuArrowRight />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>

            {!inspectionForm?.length && (
              <div className="text-center p-5 text-sm text-gray-500 ">
                <LuLibrary className="text-[50px] w-full" />
                <p> No record found</p>
              </div>
            )}
          </div>

          {/* <!-- ROW PAGINATION --> */}
          <div className="flex justify-center gap-3 flex-wrap">
            {/* Previous Button */}
            <button
              onClick={handlePrevious}
              disabled={currentPage === 1}
              className={`bg-[#fff] text-gray-500 text-xs p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] font-normal ${
                currentPage === 1
                  ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                  : ""
              }`}
            >
              <span>Previous</span>
            </button>

            {/* Left Arrow */}
            <button
              disabled={currentPage === 1}
              onClick={handlePrevious}
              className={`bg-[#fff] text-gray-400 p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] text-base font-medium ${
                currentPage === 1
                  ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                  : ""
              }`}
            >
              <LuChevronLeft className="text-xl" />
            </button>

            {/* Page Numbers */}
            {Array.from(
              { length: lastPage > 7 ? 3 : lastPage },
              (_, i) => i + 1
            ).map((page) => (
              <button
                title={`page ${page}`}
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`p-1 px-3 rounded-lg border-2 border-solid border-[#F2F1F1] font-normal text-xs ${
                  page === currentPage
                    ? "bg-red-600 text-white"
                    : "bg-[#fff] text-gray-600"
                }`}
              >
                <span>{page}</span>
              </button>
            ))}

            {lastPage > 7 && (
              <>
                {/* ellipse Arrow */}
                <button
                  disabled
                  className={`bg-[#fff] text-gray-600 p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1]`}
                >
                  <LuMoreHorizontal className="text-xl" />
                </button>

                {/* Page Numbers */}
                {Array.from({ length: 3 }, (_, i) => 2 - i).map((page) => (
                  <button
                    title={`page ${lastPage - page}`}
                    key={lastPage - page}
                    onClick={() => setCurrentPage(lastPage - page)}
                    className={`p-1 px-3 rounded-lg border-2 border-solid border-[#F2F1F1] text-base font-normal text-xs ${
                      lastPage - page === currentPage
                        ? "bg-red-600 text-white"
                        : "bg-[#fff] text-gray-600"
                    }`}
                  >
                    <span>{lastPage - page}</span>
                  </button>
                ))}
              </>
            )}

            {/* Right Arrow */}
            <button
              disabled={currentPage === lastPage}
              onClick={handleNext}
              className={`bg-[#fff] text-gray-400 p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] ${
                currentPage === lastPage
                  ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                  : ""
              }`}
            >
              <LuChevronRight className="text-xl" />
            </button>

            {/* Next Button */}
            <button
              onClick={handleNext}
              disabled={currentPage === lastPage}
              className={`bg-[#fff] text-gray-500 text-xs p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] font-normal ${
                currentPage === lastPage
                  ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                  : ""
              }`}
            >
              <span>Next</span>
            </button>
          </div>
        </div>
      </section>

      <ToastContainer />
      <Preloader loading={loading} />
    </>
  );
};

export default InspectionApprovalHistory;
